import type { ShopVendorPublic, ShopVendorThreadWithBuyer } from '@pc/db';

/** Primary label for a buyer-side thread row (company name, then @handle). */
export function buyerThreadVendorLabel(
  vendor: ShopVendorPublic | undefined,
): string {
  if (!vendor) return 'Vendor';
  return vendor.company_name?.trim() || `@${vendor.slug}`;
}

/**
 * Vendor-side thread row label. Prefers "Org Name -- User Name" when
 * both are available; falls back gracefully through user name, email,
 * org name, and finally a generic "Buyer" if nothing is set (which
 * should only happen on data anomalies, never in normal flow).
 */
export function vendorThreadBuyerLabel(
  thread: Pick<
    ShopVendorThreadWithBuyer,
    | 'buyer_org_name'
    | 'buyer_user_display_name'
    | 'buyer_user_email'
  >,
): string {
  const org = thread.buyer_org_name?.trim() ?? '';
  const user =
    thread.buyer_user_display_name?.trim() ||
    thread.buyer_user_email?.trim() ||
    '';
  if (org && user) return `${org} \u2014 ${user}`;
  if (user) return user;
  if (org) return org;
  return 'Buyer';
}

/**
 * Secondary label used as a subhead under the primary one (the buyer's
 * email when we already showed the display name, or the org name when
 * we already showed the org-user composite). Returns undefined when
 * there is no useful second line.
 */
export function vendorThreadBuyerSubLabel(
  thread: Pick<
    ShopVendorThreadWithBuyer,
    | 'buyer_org_name'
    | 'buyer_org_slug'
    | 'buyer_user_display_name'
    | 'buyer_user_email'
  >,
): string | undefined {
  const email = thread.buyer_user_email?.trim();
  const orgSlug = thread.buyer_org_slug?.trim();
  if (email && (thread.buyer_user_display_name?.trim() || thread.buyer_org_name?.trim())) {
    return email;
  }
  if (orgSlug) return `@${orgSlug}`;
  return undefined;
}
